import {
    HubPersona,
    HubCollection,
    isSupportedPersona,
    HubPerson,
    isHubPerson,
    PersonType,
    normalizeRelevancyScore,
} from '../../../shared';
import type {
    SearchResultState,
    SearchPersonaEmailsEntryState,
} from '../../state/data/searchState';

/**
 * Merge string arrays of hubPersonaIds, and return array of uniq ids.
 */
export function mergeSearchResults(
    existingResults: SearchResultState,
    newResults: HubPersona[],
    isRecipientCache: boolean
): SearchResultState {
    if (newResults) {
        let returnResults = { ...existingResults };
        newResults.filter(isSupportedPersona).forEach(persona => {
            if (
                persona.type === PersonType.PrivateDistributionList ||
                persona.type === PersonType.PublicDistributionList ||
                persona.type === PersonType.Group ||
                persona.type === PersonType.GroupBasicData
            ) {
                returnResults = addDistributionListToResults(persona, returnResults);
            } else if (isHubPerson(persona)) {
                let addToResults: boolean = true;
                let existingRelevancyScore: number = getExistingRelevancyScoreFromHubPersonaId(
                    existingResults,
                    persona
                );

                if (returnResults.personaEmailMap) {
                    Object.keys(returnResults.personaEmailMap).forEach(existingHubPersonaId => {
                        // Check if persona with same email address and isRecipientCache exists. If an entry exists, remove it
                        if (
                            existingEntryMatchEmailAndIsRecipientCache(
                                returnResults.personaEmailMap[existingHubPersonaId],
                                persona
                            )
                        ) {
                            if (isRecipientCache) {
                                // Do nothing, person with same email exists, do not replace with recipient cache version
                                addToResults = false;
                            } else {
                                existingRelevancyScore =
                                    returnResults.personaEmailMap[existingHubPersonaId]
                                        .relevancyScore;
                                returnResults = removeExistingSearchResultEntry(
                                    returnResults,
                                    existingHubPersonaId
                                );
                            }
                        }
                    });
                }
                // Only add new entry if it does not exist. Since we're searching multiple source, it may already have been added.
                if (addToResults && !returnResults.personaEmailMap?.[persona.hubPersonaId]) {
                    returnResults = addPersonToResults(
                        persona,
                        isRecipientCache,
                        returnResults,
                        existingRelevancyScore
                    );
                }
            }
        });

        return returnResults;
    }

    return existingResults;
}

function addDistributionListToResults(
    distributionList: HubCollection,
    existingResults: SearchResultState
): SearchResultState {
    return {
        ...existingResults,
        personaEmailMap: {
            ...existingResults.personaEmailMap,
            [distributionList.hubPersonaId]: {
                emailAddresses: [],
                isRecipientCache: false,
                relevancyScore: normalizeRelevancyScore(distributionList.relevancyScore),
            },
        },
    };
}

function addPersonToResults(
    person: HubPerson,
    isRecipientCache: boolean,
    existingResults: SearchResultState,
    existingRelevancyScore: number
): SearchResultState {
    return {
        ...existingResults,
        personaEmailMap: {
            ...existingResults.personaEmailMap,
            [person.hubPersonaId]: {
                emailAddresses: person.info.allEmailAddresses,
                isRecipientCache: isRecipientCache,
                relevancyScore: Math.max(
                    existingRelevancyScore,
                    normalizeRelevancyScore(person.relevancyScore)
                ),
            },
        },
    };
}

/**
 * Get existing relevancy score by hubPersonaId. Return default score if none exists.
 */
function getExistingRelevancyScoreFromHubPersonaId(
    existingResults: SearchResultState,
    hubPersona: HubPersona
): number {
    return normalizeRelevancyScore(existingResults[hubPersona.hubPersonaId]);
}

/**
 * Check if existing search result entry is recipient cache, and matches any of person's email addresses.
 */
function existingEntryMatchEmailAndIsRecipientCache(
    existingEntry: SearchPersonaEmailsEntryState,
    person: HubPerson
): boolean {
    return (
        existingEntry.isRecipientCache &&
        existingEntry.emailAddresses.some(existingEmailAddress =>
            person.info.allEmailAddresses.some(
                personaEmail => existingEmailAddress === personaEmail
            )
        )
    );
}

/**
 * Remove existing recipient cache entry from results.
 */
function removeExistingSearchResultEntry(
    returnResults: SearchResultState,
    existinghubPersonaId: string
): SearchResultState {
    returnResults.personaEmailMap = { ...returnResults.personaEmailMap };
    delete returnResults.personaEmailMap[existinghubPersonaId];
    return returnResults;
}
